/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useImperativeHandle, useRef, useState } from "react";
import { Box } from "@mui/material";
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import {
    StripeCardCvcElement,
    StripeCardCvcElementChangeEvent,
    StripeCardExpiryElement,
    StripeCardExpiryElementChangeEvent,
    StripeCardNumberElement,
    StripeCardNumberElementChangeEvent,
    StripeElementStyle } from "@stripe/stripe-js";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import TextInput from "../TextInput";
import StripeCardElementProps from "./StripeCardElementProps";

interface StripeInputProps {
    component: typeof CardNumberElement | typeof CardCvcElement | typeof CardExpiryElement,
    inputRef: any,
    stripeOnChange: (event: StripeCardNumberElementChangeEvent | StripeCardCvcElementChangeEvent | StripeCardExpiryElementChangeEvent) => void,
}

export const StripeInput = ({ component: Component, inputRef, stripeOnChange, ...props }: StripeInputProps) => {
    const elementRef = useRef<StripeCardNumberElement | StripeCardCvcElement | StripeCardExpiryElement>();
    useImperativeHandle(inputRef, () => ({
        focus: () => {
            if (elementRef.current) {
                elementRef.current.focus();
            }
        }
    }));
    return (
        <Component
            {...props}
            onReady={(element: StripeCardNumberElement | StripeCardCvcElement | StripeCardExpiryElement) => (elementRef.current = element)}
            onChange={stripeOnChange}
        />
    )
}

function getErrors(error?: string) {
    return error ? [error] : [];
}

const stripeElementStyle: StripeElementStyle = {
    base: { fontSize: "16px" },
    empty: { "::placeholder": { color: "#b0b0b0" } },
};

// Stripe payment form with ReactJS and Material-UI — Part 4
//     https://javascript.plainenglish.io/stripe-payment-form-with-reactjs-and-material-ui-part-4-118e60fca962
// Dokumentation zu React Stripe.js
//     https://stripe.com/docs/stripe-js/react
// Zukünftige Zahlungen einrichten
//     https://stripe.com/docs/payments/save-and-reuse?html-or-react=react
// React + Stripe - Accept payment through PaymentIntent API and save card details
//     https://javascript.plainenglish.io/react-stripe-accept-payment-through-paymentintent-api-and-save-card-details-aa90c55fcdb2
// Integrating Stripe in your React app with React Stripe.js
//     https://blog.logrocket.com/integrating-stripe-react-stripe-js/
// CodeSandbox mit Beispielen von Stripe
//     https://codesandbox.io/s/react-stripe-official-q1loc?fontsize=14&hidenavigation=1&theme=dark
// This example shows you how to set up React Stripe.js and use Elements.
//     https://github.com/stripe/react-stripe-js/blob/master/examples/hooks/1-Card-Detailed.js
// Stripe Payment Element
//     https://stripe.com/docs/payments/payment-element
// Aktualisieren von Kartenangaben mit einem SetupIntent
//     https://support.stripe.com/questions/update-card-details-with-a-setupintent
// How To Call Child Function From Parent Component In React
//     https://dilshankelsen.com/call-child-function-from-parent-component-in-react/
export default function StripeCardElement(props: StripeCardElementProps) {
    const { t } = useTranslation();

    const [cardNumberError, setCardNumberError] = useState<string>();
    const [cardCvcError, setCardCvcError] = useState<string>();
    const [cardExpiryError, setCardExpiryError] = useState<string>();

    const cardNumberIsComplete = useRef<boolean>(false);
    const cardCvcIsComplete = useRef<boolean>(false);
    const cardExpiryIsComplete = useRef<boolean>(false);

    return (
        <Box
            sx={{
                minWidth: "350px",
                ...(props.hidden && { display: "none" })
            }}
        >
            <TextInput
                caption={t(translationKeys.stripeCardElement.nummer)}
                feldBezeichnung=""
                typBezeichnung=""
                errors={getErrors(cardNumberError)}
                InputProps={{
                    inputComponent: StripeInput as any,
                    inputProps: {
                        component: CardNumberElement,
                        stripeOnChange: (e: StripeCardNumberElementChangeEvent) => {
                            setCardNumberError(e.error?.message);
                            cardNumberIsComplete.current = e.complete;
                            props.onChange(e.complete && cardExpiryIsComplete.current && cardCvcIsComplete.current);
                        },
                        options: { style: stripeElementStyle, placeholder: t(translationKeys.stripeCardElement.nummerPlaceholder) },
                    },
                }}
                required={props.required}
            />
            <TextInput
                caption={t(translationKeys.stripeCardElement.pruefziffer)}
                feldBezeichnung=""
                typBezeichnung=""
                errors={getErrors(cardCvcError)}
                InputProps={{
                    inputComponent: StripeInput as any,
                    inputProps: {
                        component: CardCvcElement,
                        stripeOnChange: (e: StripeCardNumberElementChangeEvent) => {
                            setCardCvcError(e.error?.message);
                            cardCvcIsComplete.current = e.complete;
                            props.onChange(cardNumberIsComplete.current && e.complete && cardExpiryIsComplete.current);
                        },
                        options: { style: stripeElementStyle, placeholder: t(translationKeys.stripeCardElement.pruefzifferPlaceholder) },
                    },
                }}
                required={props.required}
            />
            <TextInput
                caption={t(translationKeys.stripeCardElement.gueltigBis)}
                feldBezeichnung=""
                typBezeichnung=""
                errors={getErrors(cardExpiryError)}
                InputProps={{
                    inputComponent: StripeInput as any,
                    inputProps: {
                        component: CardExpiryElement,
                        stripeOnChange: (e: StripeCardNumberElementChangeEvent) => {
                            setCardExpiryError(e.error?.message);
                            cardExpiryIsComplete.current = e.complete;
                            props.onChange(cardNumberIsComplete.current && cardCvcIsComplete.current && e.complete);
                        },
                        options: { style: stripeElementStyle, placeholder: t(translationKeys.stripeCardElement.gueltigBisPlaceholder) },
                    },
                }}
                required={props.required}
            />
        </Box>
    );
}
